import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { authHeaders, formatDateTime, wtNavItems } from "./wtShared";

const WtStart = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [berufe, setBerufe] = useState([]);
  const [jobQuery, setJobQuery] = useState("");
  const [selectedBeruf, setSelectedBeruf] = useState(null);
  const [categoryPreview, setCategoryPreview] = useState([]);
  const [searchHint, setSearchHint] = useState("Beruf eingeben, um passende offizielle Bezeichnungen zu finden.");
  const [loading, setLoading] = useState(true);
  const [startLoading, setStartLoading] = useState("");
  const [error, setError] = useState("");

  const activeRun = useMemo(
    () => (history || []).find((h) => h.status !== "submitted") || null,
    [history]
  );

  useEffect(() => {
    document.title = "Experts4Europe | Wissenstest";
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const histRes = await axios.get(`${config.API_BASE_URL}/api/wt/me/attempts`, { headers: authHeaders() });
        setHistory(histRes.data?.rows || []);
      } catch (e) {
        setError(e.response?.data?.message || "Wissenstest konnte nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (jobQuery.trim().length < 2) {
        setBerufe([]);
        setSearchHint("Bitte mindestens 2 Zeichen eingeben.");
        return;
      }
      try {
        const res = await axios.get(`${config.API_BASE_URL}/api/stb/berufe`, { params: { q: jobQuery, limit: 40 } });
        const rows = Array.isArray(res.data) ? res.data : [];
        setBerufe(rows);
        setSearchHint(rows.length ? `Treffer: ${rows.length}` : "Keine Treffer.");
      } catch (_e) {
        setBerufe([]);
        setSearchHint("Berufssuche aktuell nicht erreichbar.");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [jobQuery]);

  useEffect(() => {
    (async () => {
      if (!selectedBeruf?.beruf) {
        setCategoryPreview([]);
        return;
      }
      const family = encodeURIComponent(String(selectedBeruf.beruf).split(" - ")[0].trim());
      const specialization = String(selectedBeruf.beruf).includes(" - ")
        ? String(selectedBeruf.beruf).split(" - ").slice(1).join(" - ").trim()
        : "";
      try {
        const res = await axios.get(`${config.API_BASE_URL}/api/wt/professions/${family}/categories`, {
          params: specialization ? { specialization } : {},
        });
        setCategoryPreview(res.data?.rows || []);
      } catch (_e) {
        setCategoryPreview([]);
      }
    })();
  }, [selectedBeruf]);

  const startTest = async (mode) => {
    try {
      setStartLoading(mode);
      const payload = {
        test_mode: mode,
        num_questions: 10,
      };
      if (mode === "professional" && selectedBeruf?.beruf) {
        payload.beruf_text = selectedBeruf.beruf;
        payload.branche = selectedBeruf.beruf;
      }
      const res = await axios.post(`${config.API_BASE_URL}/api/wt/attempts`, payload, { headers: authHeaders() });
      const runId = res.data?.attempt?.id;
      if (runId) navigate(`/assessment/wt/run/${runId}`);
    } catch (e) {
      setError(e.response?.data?.message || "Testlauf konnte nicht gestartet werden.");
    } finally {
      setStartLoading("");
    }
  };

  return (
    <CockpitShell
      title="Wissenstest"
      subtitle="Unter Zeitdruck pruefst du dein Wissen. Du bekommst direkt ein Ergebnis."
      badge="Applicant Assessment"
      navItems={wtNavItems}
      actions={
        <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={() => navigate("/assessment/wt/history")}>
          Meine Ergebnisse
        </Button>
      }
    >
      {error ? <p className="rounded-lg border border-rose-300/30 bg-rose-500/10 p-3 text-sm text-rose-100">{error}</p> : null}

      {activeRun ? (
        <Card className="card-e4e border-cyan-300/35">
          <CardHeader><CardTitle className="font-display text-white">Du hast einen Testlauf begonnen</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-200">
            <p>Letzter Stand: {activeRun.test_name || "Wissenstest"} · gestartet am {formatDateTime(activeRun.started_at)}</p>
            <p className="text-xs text-slate-300">Du kannst sofort fortsetzen.</p>
            <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={() => navigate(`/assessment/wt/run/${activeRun.id}`)}>
              Fortsetzen
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card className="card-e4e">
        <CardHeader><CardTitle className="font-display text-white">Wissenstest starten</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {loading ? <div className="h-28 animate-pulse rounded-xl border border-white/10 bg-white/5" /> : null}
          {!loading ? (
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
              <p className="text-xl font-semibold text-white">Allgemein</p>
              <p className="mt-1 text-sm text-slate-300">Allgemeine Fach- und Alltagsthemen, 10 Fragen.</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-300">
                <Badge variant="outline" className="border-white/20 bg-white/5 text-slate-100">10 Fragen</Badge>
                <Badge variant="outline" className="border-white/20 bg-white/5 text-slate-100">Timer pro Frage</Badge>
              </div>
              <Button className="mt-4 bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={() => startTest("general")} disabled={startLoading !== ""}>
                {startLoading === "general" ? "Startet..." : "Allgemeinen Test starten"}
              </Button>
            </div>
          ) : null}

          {!loading ? (
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
              <p className="text-xl font-semibold text-white">Berufsbezogen</p>
              <p className="mt-1 text-sm text-slate-300">Fragen zu Berufsfeldern. Waehle eine offizielle Berufsbezeichnung.</p>
              <div className="mt-3 grid gap-2">
                <Input
                  value={jobQuery}
                  onChange={(e) => setJobQuery(e.target.value)}
                  placeholder="Beruf suchen (z. B. Pflegefachkraft)"
                  className="border-white/20 bg-slate-950/40 text-white placeholder:text-slate-400"
                />
                <p className="text-xs text-slate-300">{searchHint}</p>
                {berufe.length ? (
                  <div className="max-h-52 overflow-auto rounded-xl border border-white/10 bg-slate-950/35 p-2">
                    {berufe.map((item) => (
                      <button
                        key={`${item.id}-${item.beruf}`}
                        type="button"
                        onClick={() => setSelectedBeruf(item)}
                        className={`mb-1 w-full rounded px-2 py-1 text-left text-sm ${
                          selectedBeruf?.beruf === item.beruf
                            ? "bg-cyan-500/20 text-cyan-100"
                            : "text-slate-200 hover:bg-white/10"
                        }`}
                      >
                        {item.beruf}
                      </button>
                    ))}
                  </div>
                ) : null}
                {selectedBeruf?.beruf ? (
                  <p className="text-xs text-cyan-100">Ausgewaehlter Beruf: {selectedBeruf.beruf}</p>
                ) : null}
                {categoryPreview.length ? (
                  <div className="rounded-xl border border-white/10 bg-slate-900/40 p-2">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Kategorien (Vorschau)</p>
                    <div className="flex flex-wrap gap-2">
                      {categoryPreview.slice(0, 8).map((row) => (
                        <Badge key={row.category} variant="outline" className="border-white/20 bg-white/5 text-slate-100">
                          {row.category} ({row.question_count})
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
              <p className="mt-2 text-xs text-slate-300">Du hast pro Frage nur wenige Sekunden Zeit.</p>
              <Button className="mt-4 bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={() => startTest("professional")} disabled={startLoading !== ""}>
                {startLoading === "professional" ? "Startet..." : "Berufstest starten"}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </CockpitShell>
  );
};

export default WtStart;
